'use client';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import useEligibleCarCount from '../hooks/useEligibleCarCount';
import type { IPrequalResult, PersonalFinanceEnvironment } from '../types';
import {
  calculateDaysRemaining,
  formatCarCount,
  formatExpirationDate,
} from '../utils/personalFinanceUtilities';
import CountdownCircle from './CountdownCircle';

interface IPrequalResultProps {
  prequalResult: IPrequalResult;
  environment: PersonalFinanceEnvironment;
  onInvalidPrequal: () => void;
}

export default function PrequalResult({
  prequalResult,
  environment,
  onInvalidPrequal,
}: IPrequalResultProps) {
  const decisionedDate = prequalResult.metaData.decisionedDate;
  const daysRemaining = calculateDaysRemaining(decisionedDate);
  const carCountQuery = useEligibleCarCount(environment);

  useEffect(() => {
    if (daysRemaining === null || daysRemaining <= 0) {
      onInvalidPrequal();
    }
  }, [daysRemaining, onInvalidPrequal]);

  if (daysRemaining === null || daysRemaining <= 0) {
    return null;
  }

  const carCount = carCountQuery.data;
  const hasCarCount = typeof carCount === 'number' && carCount > 0;
  const expirationDate = formatExpirationDate(decisionedDate);

  return (
    <Card
      id="pfc-v2-prequal-result"
      elevation={2}
      sx={{
        borderRadius: '24px',
        maxWidth: '1536px',
        width: '100%',
        margin: '0 auto',
      }}
    >
      <Stack
        sx={{
          gap: 3,
          padding: '24px',
          '@media (min-width:600px)': {
            flexDirection: 'row',
            gap: 5,
            padding: '56px 40px',
            alignItems: 'center',
          },
        }}
      >
        <CountdownCircle daysRemaining={daysRemaining} />
        <Stack sx={{ gap: 2, flex: 1 }}>
          <Typography variant="body1" component="p">
            You&apos;ve unlocked personalized payments{' '}
            {hasCarCount && (
              <>
                on{' '}
                <Typography component="span" sx={{ fontWeight: 700 }}>
                  {formatCarCount(carCount)}
                </Typography>{' '}
                cars{' '}
              </>
            )}
            until{' '}
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {expirationDate}.
            </Typography>
          </Typography>
          <Button
            href="/cars/all"
            variant="contained"
            size="large"
            id="pfc-v2-prequal-cta"
            sx={{ width: { xs: '100%', lg: 'fit-content' } }}
          >
            Shop with my terms
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
}
